import { collection, getDoc, getDocs, query, where, doc } from 'firebase/firestore';
import { apiGet, apiGetAllPages, useApiData } from './apiClient';
import { getFirstStockId } from '../utils/eventSessions';

const ORGANISATION_OR_VENUE_SALE_CHANNELS = ['vm', 'vp', 'ocm', 'op'];

/** Firestore doc uyumlulugu: { data: () => sale } */
const toSaleDocShim = (sale) => ({
  id: sale.id,
  data: () => sale,
});

export const fetchEventById = async (db, eventId) => {
  if (useApiData) {
    return apiGet(`events/${encodeURIComponent(eventId)}`);
  }
  const eventDoc = doc(db, 'events-v2', eventId);
  const eventSnap = await getDoc(eventDoc);
  if (!eventSnap.exists()) return null;
  return { id: eventSnap.id, ...eventSnap.data() };
};

export const fetchStockForEvent = async (db, event, stockId) => {
  const effectiveStockId = stockId || getFirstStockId(event);
  if (!effectiveStockId) return null;

  if (useApiData) {
    try {
      const row = await apiGet(`stocks/${encodeURIComponent(effectiveStockId)}`);
      return row?.id ? row : { id: effectiveStockId, ...row };
    } catch (error) {
      if (error.status === 404) return null;
      throw error;
    }
  }

  const stockDoc = doc(db, 'stocks', effectiveStockId);
  const stockSnap = await getDoc(stockDoc);
  if (!stockSnap.exists()) return null;
  return { id: stockSnap.id, ...stockSnap.data() };
};

export const fetchSalesForEvent = async (db, eventId, stockId) => {
  if (!eventId) return [];

  if (useApiData) {
    try {
      const qs = new URLSearchParams();
      if (stockId) qs.set('stockId', stockId);
      const report = await apiGet(
        `events/${encodeURIComponent(eventId)}/sale-report?${qs.toString()}`,
      );
      if (Array.isArray(report?.sales)) {
        return report.sales.map(toSaleDocShim);
      }
    } catch (error) {
      // endpoint yoksa eski yola dus
      if (error.status !== 404) {
        console.warn('sale-report endpoint failed, falling back', error?.message);
      }
    }

    const qs = new URLSearchParams({ sort: 'createdate', order: 'desc' });
    if (stockId) {
      qs.set('event.id', eventId);
      qs.set('event.stockID', stockId);
    } else {
      qs.set('event.id', eventId);
    }
    const rows = await apiGetAllPages('sales', qs);
    return rows.map(toSaleDocShim);
  }

  let qRef;
  if (stockId) {
    qRef = query(
      collection(db, 'sales'),
      where('event.id', '==', eventId),
      where('event.stockID', '==', stockId),
    );
  } else {
    qRef = query(collection(db, 'sales'), where('event.id', '==', eventId));
  }
  const snapshot = await getDocs(qRef);
  return snapshot.docs;
};

export const buildSalesDataGrid = (event, stock, salesDocs) => {
  if (!event || !stock || !Array.isArray(salesDocs)) return { grid: [], byProduct: {} };

  const salesByProduct = {};

  salesDocs.forEach((docSnap) => {
    const sale = docSnap.data();
    if (sale.status !== 1) return;
    if (!sale.details) return;

    Object.keys(sale.details).forEach((productCategory) => {
      const items = Object.values(sale.details[productCategory] || {});
      items.forEach((product) => {
        const isOrgOrVenue = ORGANISATION_OR_VENUE_SALE_CHANNELS.includes(sale.channel);
        const productKey = product?.label?.key;
        const stockProduct = stock?.stock?.[productCategory]?.find((r) => r.id === productKey);
        if (!productKey || !stockProduct) return;

        const commissionExtra = isOrgOrVenue ? false : event.isCommissionExtra ? true : false;
        const unitPrice = stockProduct.price;
        const commissionAdd = commissionExtra
          ? (unitPrice * product.count * event.commissionFee) / 100
          : 0;
        const computedPrice = unitPrice * product.count + commissionAdd;

        if (!salesByProduct[productKey]) {
          salesByProduct[productKey] = {
            sold: 0,
            totalSale: 0,
            price:
              unitPrice + (event.isCommissionExtra ? (unitPrice * event.commissionFee) / 100 : 0),
            stock: stockProduct.stock,
            title: product?.label?.title?.en,
            category: productCategory,
            hasDownPayment: !!stockProduct.hasDownPayment,
            downPaymentAmount: stockProduct.downPaymentAmount ? stockProduct.downPaymentAmount : 0,
          };
        }

        salesByProduct[productKey].sold += product.count;
        salesByProduct[productKey].totalSale += computedPrice;
      });
    });
  });

  const grid = Object.keys(salesByProduct).map((productID) => ({
    id: productID,
    title: salesByProduct[productID].title,
    category: salesByProduct[productID].category,
    price: salesByProduct[productID].hasDownPayment
      ? salesByProduct[productID].downPaymentAmount
      : salesByProduct[productID].price,
    stock: salesByProduct[productID].stock,
    sold: salesByProduct[productID].sold,
    totalSale: salesByProduct[productID].totalSale,
    remaining: salesByProduct[productID].stock - salesByProduct[productID].sold,
  }));

  let totalSold = 0;
  let totalSale = 0;
  grid.forEach((p) => {
    totalSold += p.sold;
    totalSale += p.totalSale ? p.totalSale : p.sold * p.price;
  });
  grid.push({
    id: 'total',
    category: 'Total',
    title: '',
    price: '',
    stock: '',
    sold: totalSold,
    totalSale,
  });

  return { grid, byProduct: salesByProduct };
};

export const buildCouponsData = (event) => {
  if (!event?.coupons) return [];
  const couponsData = [];
  event.coupons.forEach((coupon) => {
    couponsData.push({
      id: coupon.code.toLowerCase().concat(Math.random().toString(36).substring(7)),
      code: coupon.code,
      discount: coupon.discount,
      stock: coupon.stock,
      used: coupon.used,
    });
  });

  let totalDiscount = 0;
  let totalUsed = 0;
  couponsData.forEach((c) => {
    if (c.used) {
      totalDiscount += c.discount * c.used;
      totalUsed += c.used ? c.used : 0;
    }
  });
  couponsData.push({
    id: 'total',
    code: 'Total',
    stock: '',
    used: '',
    remaining: '',
    discount: '',
    totalDiscount,
    totalUsed,
  });
  return couponsData;
};

export const buildRemainingPayments = (stock, salesDocs) => {
  if (!stock || !Array.isArray(salesDocs)) return [];
  const rows = [];

  salesDocs.forEach((docSnap) => {
    const sale = docSnap.data();
    if (!sale.details) return;
    const saleId = docSnap.id || sale.id || '';
    Object.keys(sale.details).forEach((productCategory) => {
      const items = Object.values(sale.details[productCategory] || {});
      items.forEach((product) => {
        const productKey = product?.label?.key;
        const stockProduct = stock?.stock?.[productCategory]?.find((r) => r.id === productKey);
        if (!productKey || !stockProduct || !stockProduct.hasDownPayment) return;
        const remainingPerUnit =
          Number(stockProduct.price || 0) - Number(stockProduct.downPaymentAmount || 0);
        const sold = Number(product.count || 0);
        rows.push({
          id: `${saleId}-${productKey}-${rows.length}`,
          category: productCategory,
          title: product?.label?.title?.en,
          price: stockProduct.price,
          stock: stockProduct.stock,
          sold,
          downPaymentAmount: stockProduct.downPaymentAmount,
          totalRmainingAmount: sold * remainingPerUnit,
        });
      });
    });
  });

  let totalSold = 0;
  let totalRmainingAmount = 0;
  rows.forEach((p) => {
    totalSold += p.sold;
    totalRmainingAmount += Number(p.totalRmainingAmount || 0);
  });
  rows.push({
    id: 'total',
    category: 'Total',
    title: '',
    price: '',
    stock: '',
    sold: totalSold,
    totalRmainingAmount,
  });
  return rows;
};

export const computeOrganisationOrVenueSales = (event, salesDocs) => {
  if (!event || !Array.isArray(salesDocs)) return 0;
  let total = 0;
  const isCommissionExtra = event.isCommissionExtra ? true : false;
  salesDocs.forEach((docSnap) => {
    const sale = docSnap.data();
    if (sale.status !== 1) return;
    if (!ORGANISATION_OR_VENUE_SALE_CHANNELS.includes(sale.channel)) return;
    const saleAmount = sale.paymentTotal;
    let amountToBeAdded = saleAmount - (event.servicefee ? event.servicefee : 0);
    if (isCommissionExtra) {
      const saleCommission = saleAmount * (1 - 100 / (100 + event.commissionFee));
      amountToBeAdded = amountToBeAdded - saleCommission;
    }
    total += amountToBeAdded;
  });
  return total;
};

const LINE_KEYS = ['affiliate', 'salePoint', 'promoter', 'managerCash'];

const emptyLines = () =>
  LINE_KEYS.map((key) => ({
    key,
    label:
      key === 'affiliate'
        ? 'Affiliate satışları'
        : key === 'salePoint'
          ? 'Sales Point satışları'
          : key === 'promoter'
            ? 'Promoter satışları'
            : 'Manager nakit',
    amount: 0,
    count: 0,
    sellers: [],
  }));

const GISE_LINE_KEYS = ['website', 'mobile', 'saleAgent', 'affiliate', 'salePoint', 'other'];

const emptyGiseLines = () =>
  GISE_LINE_KEYS.map((key) => ({
    key,
    label:
      key === 'website'
        ? 'Website'
        : key === 'mobile'
          ? 'Mobile'
          : key === 'saleAgent'
            ? 'Sale Agent'
            : key === 'affiliate'
              ? 'Affiliate (bağlı değil)'
              : key === 'salePoint'
                ? 'Sales Point (bağlı değil)'
                : 'Diğer',
    amount: 0,
    count: 0,
    sellers: [],
  }));

const getSaleChannelId = (sale) => {
  if (sale?.saleChannelID != null) {
    return typeof sale.saleChannelID === 'object'
      ? sale.saleChannelID.code || sale.saleChannelID.id || null
      : sale.saleChannelID;
  }
  if (sale?.extras?.saleChannelID != null) {
    const v = sale.extras.saleChannelID;
    return typeof v === 'object' ? v.code || v.id || null : v;
  }
  return null;
};

const saleAmountForReport = (event, sale) => {
  const saleAmount = Number(sale.paymentTotal || 0);
  if (!ORGANISATION_OR_VENUE_SALE_CHANNELS.includes(sale.channel)) {
    return saleAmount;
  }
  let amount = saleAmount - (event.servicefee ? Number(event.servicefee) : 0);
  if (event.isCommissionExtra) {
    const saleCommission = saleAmount * (1 - 100 / (100 + (event.commissionFee || 0)));
    amount -= saleCommission;
  }
  return amount;
};

const bumpLine = (lines, key, amount, sellerMeta = null) => {
  const line = lines.find((l) => l.key === key);
  if (!line) return;
  line.amount += amount;
  line.count += 1;
  if (!sellerMeta) return;
  if (!Array.isArray(line.sellers)) line.sellers = [];
  const sellerKey = sellerMeta.id || sellerMeta.name || 'unknown';
  let seller = line.sellers.find((s) => s.id === sellerKey);
  if (!seller) {
    seller = {
      id: sellerKey,
      name: sellerMeta.name || sellerKey,
      userId: sellerMeta.userId || null,
      userName: sellerMeta.userName || '',
      userEmail: sellerMeta.userEmail || '',
      userMobile: sellerMeta.userMobile || '',
      channel: sellerMeta.channel || null,
      amount: 0,
      count: 0,
    };
    line.sellers.push(seller);
  }
  seller.amount += amount;
  seller.count += 1;
};

const sellerMetaFromSale = (channel, channelId, lookups = {}) => {
  if (!channelId) {
    return { id: channel || 'unknown', name: channel || 'Bilinmiyor', channel };
  }
  const affiliates = lookups.affiliates || new Map();
  const salePoints = lookups.salePoints || new Map();
  const users = lookups.users || new Map();

  if (channel === 'a') {
    const aff = affiliates.get(channelId) || affiliates.get(String(channelId).toLowerCase());
    const user = aff?.user ? users.get(String(aff.user)) : null;
    return {
      id: channelId,
      name: aff?.name || channelId,
      userId: aff?.user || null,
      userName: user
        ? [user.name, user.surname].filter(Boolean).join(' ')
        : '',
      userEmail: user?.email || '',
      userMobile: user?.mobile || '',
      channel,
    };
  }
  if (channel === 'sp') {
    const sp = salePoints.get(channelId);
    return {
      id: channelId,
      name: sp?.name || channelId,
      userId: null,
      userName: '',
      userEmail: '',
      userMobile: '',
      channel,
    };
  }
  // vp, op, vm, ocm, sa → typically user id
  const user = users.get(channelId);
  return {
    id: channelId,
    name: user
      ? [user.name, user.surname].filter(Boolean).join(' ') || channelId
      : channelId,
    userId: channelId,
    userName: user
      ? [user.name, user.surname].filter(Boolean).join(' ')
      : '',
    userEmail: user?.email || '',
    userMobile: user?.mobile || '',
    channel,
  };
};

/**
 * Event sahipleri: venue ve/veya organisationCompanies.
 * @returns {Promise<Array<{ type: 'venue'|'organisationCompany', id: string, name: string }>>}
 */
export const resolveEventOwners = async (event, dbRef = null) => {
  if (!event) return [];
  const owners = [];

  if (event.venue) {
    let name = event.venue;
    try {
      if (useApiData) {
        const venue = await apiGet(`venues/${encodeURIComponent(event.venue)}`);
        name = venue?.name || name;
      } else if (dbRef) {
        const venueSnap = await getDoc(doc(dbRef, 'venues', event.venue));
        if (venueSnap.exists()) name = venueSnap.data()?.name || name;
      }
    } catch {
      /* keep id as name */
    }
    owners.push({ type: 'venue', id: String(event.venue), name });
  }

  const orgIds = Array.isArray(event.organisationCompanies)
    ? event.organisationCompanies
    : [];
  for (const orgId of orgIds) {
    if (!orgId) continue;
    let name = orgId;
    try {
      if (useApiData) {
        const company = await apiGet(
          `organisationCompanies/${encodeURIComponent(orgId)}`,
        );
        name = company?.name || name;
      } else if (dbRef) {
        const companySnap = await getDoc(doc(dbRef, 'organisationCompanies', orgId));
        if (companySnap.exists()) name = companySnap.data()?.name || name;
      }
    } catch {
      /* keep id */
    }
    owners.push({
      type: 'organisationCompany',
      id: String(orgId),
      name,
    });
  }

  return owners;
};

/**
 * Promoter / affiliate / salePoint / user lookup map'leri.
 */
export const fetchOwnerLinkedLookups = async (owners, salesDocs = []) => {
  const venueIds = new Set(
    owners.filter((o) => o.type === 'venue').map((o) => o.id),
  );
  const orgIds = new Set(
    owners.filter((o) => o.type === 'organisationCompany').map((o) => o.id),
  );

  const promoterToOwner = new Map();
  const affiliates = new Map();
  const salePoints = new Map();
  const users = new Map();

  if (!useApiData) {
    return { promoterToOwner, affiliates, salePoints, users };
  }

  const channelIds = new Set();
  (salesDocs || []).forEach((docSnap) => {
    const sale = typeof docSnap.data === 'function' ? docSnap.data() : docSnap;
    if (!sale || sale.status !== 1) return;
    const id = getSaleChannelId(sale);
    if (id) channelIds.add(String(id));
  });

  try {
    const [promotersRes, affiliatesRes, salePointsRes] = await Promise.all([
      apiGet('promoters?perPage=500').catch(() => null),
      apiGet('affiliates?perPage=500').catch(() => null),
      apiGet('salePoints?perPage=500').catch(() => null),
    ]);

    const promoterRows = promotersRes?.data ?? [];
    promoterRows.forEach((p) => {
      const pVenues = Array.isArray(p.venues) ? p.venues.map(String) : [];
      const pOrgs = Array.isArray(p.organisationCompanies)
        ? p.organisationCompanies.map(String)
        : [];
      const matchedVenue = pVenues.find((id) => venueIds.has(id));
      const matchedOrg = pOrgs.find((id) => orgIds.has(id));
      const ownerId = matchedVenue || matchedOrg || null;
      if (ownerId) {
        if (p.user) promoterToOwner.set(String(p.user), ownerId);
        if (p.id) promoterToOwner.set(String(p.id), ownerId);
      }
      if (p.user) channelIds.add(String(p.user));
    });

    const affiliateRows = affiliatesRes?.data ?? [];
    affiliateRows.forEach((a) => {
      const row = {
        id: a.id,
        name: a.name || a.code || a.id,
        code: a.code || null,
        user: a.user || null,
      };
      if (a.id) affiliates.set(String(a.id), row);
      if (a.code) {
        affiliates.set(String(a.code), row);
        affiliates.set(String(a.code).toLowerCase(), row);
      }
      if (a.user) channelIds.add(String(a.user));
    });

    const spRows = salePointsRes?.data ?? [];
    spRows.forEach((sp) => {
      if (sp.id) {
        salePoints.set(String(sp.id), { id: sp.id, name: sp.name || sp.id });
      }
    });

    // Kullanici detaylari (promoter / manager / affiliate.user)
    const userIds = [...channelIds].slice(0, 80);
    await Promise.all(
      userIds.map(async (uid) => {
        try {
          const u = await apiGet(`users/${encodeURIComponent(uid)}`);
          if (u?.id) {
            users.set(String(u.id), {
              id: u.id,
              name: u.name || '',
              surname: u.surname || '',
              email: u.email || '',
              mobile: u.mobile || '',
            });
          }
        } catch {
          /* skip */
        }
      }),
    );
  } catch {
    /* lookup optional */
  }

  return { promoterToOwner, affiliates, salePoints, users };
};

/**
 * Satislari event sahibi (venue/org) veya Gise bucket'larina dagitir.
 */
export const buildOwnershipSalesBreakdown = (
  event,
  salesDocs,
  owners = [],
  lookups = {},
) => {
  const totalRevenue = (salesDocs || []).reduce((sum, docSnap) => {
    const sale = typeof docSnap.data === 'function' ? docSnap.data() : docSnap;
    if (sale?.status !== 1) return sum;
    return sum + Number(sale.paymentTotal || 0);
  }, 0);

  const ownerMap = new Map();
  owners.forEach((o) => {
    ownerMap.set(o.id, {
      ...o,
      lines: emptyLines(),
      total: 0,
    });
  });

  const gise = { lines: emptyGiseLines(), total: 0 };
  const hasVenue = owners.some((o) => o.type === 'venue');
  const orgOwnerIds = new Set(
    owners.filter((o) => o.type === 'organisationCompany').map((o) => o.id),
  );
  const venueOwner = owners.find((o) => o.type === 'venue');
  const firstOrg = owners.find((o) => o.type === 'organisationCompany');
  const promoterToOwner = lookups.promoterToOwner || new Map();

  const addToOwner = (ownerId, lineKey, amount, saleChannel, channelId) => {
    const block = ownerMap.get(ownerId);
    if (!block) return false;
    const meta = sellerMetaFromSale(saleChannel, channelId, lookups);
    bumpLine(block.lines, lineKey, amount, meta);
    block.total += amount;
    return true;
  };

  const addToGise = (lineKey, amount, saleChannel, channelId) => {
    const meta =
      lineKey === 'website' || lineKey === 'mobile' || lineKey === 'other'
        ? {
            id: lineKey,
            name:
              lineKey === 'website'
                ? 'Website'
                : lineKey === 'mobile'
                  ? 'Mobile'
                  : 'Diğer',
            channel: saleChannel,
          }
        : sellerMetaFromSale(saleChannel, channelId, lookups);
    bumpLine(gise.lines, lineKey, amount, meta);
    gise.total += amount;
  };

  (salesDocs || []).forEach((docSnap) => {
    const sale = typeof docSnap.data === 'function' ? docSnap.data() : docSnap;
    if (!sale || sale.status !== 1) return;

    const channel = sale.channel;
    const amount = saleAmountForReport(event, sale);
    const channelIdRaw = getSaleChannelId(sale);
    const channelId = channelIdRaw != null ? String(channelIdRaw) : null;

    if (channel === 'vm') {
      if (
        hasVenue &&
        venueOwner &&
        addToOwner(venueOwner.id, 'managerCash', amount, channel, channelId)
      )
        return;
      addToGise('other', amount, channel, channelId);
      return;
    }
    if (channel === 'vp') {
      const linked =
        (channelId && promoterToOwner.get(channelId)) ||
        (hasVenue && venueOwner ? venueOwner.id : null);
      if (linked && addToOwner(linked, 'promoter', amount, channel, channelId))
        return;
      addToGise('other', amount, channel, channelId);
      return;
    }
    if (channel === 'ocm') {
      const target =
        (channelId && orgOwnerIds.has(channelId) && channelId) ||
        firstOrg?.id ||
        null;
      if (target && addToOwner(target, 'managerCash', amount, channel, channelId))
        return;
      addToGise('other', amount, channel, channelId);
      return;
    }
    if (channel === 'op') {
      const linked =
        (channelId && promoterToOwner.get(channelId)) ||
        (channelId && orgOwnerIds.has(channelId) && channelId) ||
        firstOrg?.id ||
        null;
      if (linked && addToOwner(linked, 'promoter', amount, channel, channelId))
        return;
      addToGise('other', amount, channel, channelId);
      return;
    }
    if (channel === 'a') {
      const linked = channelId ? promoterToOwner.get(channelId) : null;
      if (linked && addToOwner(linked, 'affiliate', amount, channel, channelId))
        return;
      addToGise('affiliate', amount, channel, channelId);
      return;
    }
    if (channel === 'sp') {
      const linked = channelId ? promoterToOwner.get(channelId) : null;
      if (linked && addToOwner(linked, 'salePoint', amount, channel, channelId))
        return;
      addToGise('salePoint', amount, channel, channelId);
      return;
    }
    if (channel === 'w') {
      addToGise('website', amount, channel, channelId);
      return;
    }
    if (channel === 'm') {
      addToGise('mobile', amount, channel, channelId);
      return;
    }
    if (channel === 'sa') {
      addToGise('saleAgent', amount, channel, channelId);
      return;
    }
    addToGise('other', amount, channel, channelId);
  });

  // sellers sirala
  const sortSellers = (lines) => {
    (lines || []).forEach((line) => {
      if (Array.isArray(line.sellers)) {
        line.sellers.sort((a, b) => b.amount - a.amount);
      }
    });
  };
  ownerMap.forEach((block) => sortSellers(block.lines));
  sortSellers(gise.lines);

  return {
    totalRevenue,
    eventOwners: owners,
    owners: Array.from(ownerMap.values()),
    gise,
  };
};

export const computeSettlementNet = (entries) => {
  if (!Array.isArray(entries)) return 0;
  return entries.reduce((sum, e) => {
    const amount = Number(e?.amount || 0);
    if (e?.type === 'income') return sum + amount;
    if (e?.type === 'expense') return sum - amount;
    return sum;
  }, 0);
};
